import { Dispatch } from 'redux';

import {
  CompanyPosition,
  CreateUser,
  HelperState,
  LoginData,
  UpdateUser,
  UpdateWorkerData,
  UploadAvatarData,
  User,
  UserDashboards,
  VerifyPhoneData,
  Worker,
  S3FileData,
} from '@vyce/core/src/types';
import {
  createUserRequest,
  updateMeRequest,
  updateProfilePhotoRequest,
  userMeRequest,
} from '@vyce/core/src/api/users';
import {
  loginRequest,
  redeemVerificationCodeRequest,
  sendVerificationSMSRequest,
} from '@vyce/core/src/api/auth';
import { isEffectiveAdminPosition } from '@vyce/core/src/utils/permissions';
import { getUserDashboardsRequest, updateUserDashboardsRequest } from '@vyce/core/src/api/dashboards';
import { withDormantTeamMembersWidget } from '@vyce/core/src/views/widgets/DormantTeamMembersWidget/constants';
import { redeemActiveAdminInvitesRequest } from '@vyce/core/src/api/invites';
import { store } from '../store';
import { updateNavItems } from './helper';
import { getFromLS } from '@vyce/core/src/utils/local-storage';
import { setTokens, clearTokens, getRefreshToken } from '@vyce/core/src/api/token-storage';
import { signOutOfFirebase } from '@vyce/core/src/api/firebase-session';
import { logoutRequest } from '@vyce/core/src/api/auth';
import { uploadProfilePhotoRequest } from '@vyce/core/src/api/storage';
import { getQualificationsRequest } from '@vyce/core/src/api/checkers';
import { getWorkerRequest, updateWorkerRequest } from '@vyce/core/src/api/connect';
import { CAPTCHA_AVOIDER } from '@vyce/core/src/api/config';
import { getEmployeeDataRequest } from '@vyce/core/src/api/users';

import { ActionType } from '../action-types';
import { UserAction } from '../actions/user';
import { HelperAction } from '../actions/helper';

const updateToken = (token: string, refreshToken: string, dispatch: Dispatch<HelperAction>) => {
  setTokens(token, refreshToken);
  dispatch({
    type: ActionType.SET_ACCESS_TOKEN,
    payload: token,
  });
};

const handleMeRequest = async (): Promise<User> => {
  let userData: User = {} as User;
  let employeeData = undefined;

  const [meResult, employeeDataResult] = await Promise.allSettled([
    userMeRequest(),
    getEmployeeDataRequest(),
  ]);

  if (meResult.status === 'fulfilled') {
    userData = meResult.value.data || ({} as User);
  } else {
    console.error('Failed to fetch user data:', meResult.reason);
  }

  if (employeeDataResult.status === 'fulfilled') {
    employeeData = employeeDataResult.value.data;
  } else {
    console.error('Failed to fetch employee data:', employeeDataResult.reason);
  }

  return {
    ...userData,
    employee: employeeData,
    isCompanyAdmin: userData.positions?.some(isEffectiveAdminPosition),
  };
};

const handleUserDashboardsRequest = async (token: string): Promise<UserDashboards> => {
  const res = await getUserDashboardsRequest(token as string);
  return res.data;
};

export const isPhoneVerificationPending = (user: User): boolean =>
  !!user.phone && user.phone_verified === false;

const resolvePostAuthState = async (
  redirectUrl?: string
): Promise<{ redirect?: string; freshUser?: User }> => {
  if (redirectUrl !== '/onboarding') return { redirect: redirectUrl };
  try {
    const res = await redeemActiveAdminInvitesRequest();
    const companies = res.data || [];
    if (companies.length) {
      const freshUser = await handleMeRequest();
      return { redirect: '/dashboard', freshUser };
    }
  } catch (e) {
    console.error(e);
  }
  return { redirect: '/onboarding' };
};

const getSelectedPosition = (user: User, helper: HelperState): CompanyPosition | undefined => {
  const positions = user?.positions?.filter(isEffectiveAdminPosition) || [];
  if (!positions?.length) {
    return;
  }
  let selectedPosition: CompanyPosition | undefined = positions[0];
  const positionDataFromLS = getFromLS('selectedPosition');
  if (positionDataFromLS?.role) {
    // A stale LS entry (revoked position, another account's leftovers) must not
    // leave the company unselected — fall back to the first valid position
    selectedPosition =
      positions?.find(
        position =>
          position.role === positionDataFromLS.role && position.company.uuid === positionDataFromLS.companyId
      ) || positions[0];
  }

  return selectedPosition?.company?.uuid !== helper?.selectedPosition?.company?.uuid &&
    selectedPosition?.company?.uuid
    ? selectedPosition
    : undefined;
};

export const refreshUserAndDispatch = async (
  dispatch: Dispatch<UserAction | HelperAction>
): Promise<User> => {
  const userState = await handleMeRequest();
  const helper = store.getState().helper;
  const selectedPosition = getSelectedPosition(userState, helper);
  updateNavItems(userState, dispatch, selectedPosition);
  if (selectedPosition) {
    dispatch({
      type: ActionType.SET_SELECTED_COMPANY,
      payload: selectedPosition.company,
    });
    dispatch({
      type: ActionType.SET_SELECTED_POSITION,
      payload: selectedPosition,
    });
  }
  dispatch({
    type: ActionType.SET_USER_DATA,
    payload: userState,
  });
  return userState;
};

export const setUserData = (data: User) => {
  return (dispatch: Dispatch<UserAction>) => {
    dispatch({
      type: ActionType.SET_USER_DATA,
      payload: data,
    });
  };
};

export const clearUser = () => {
  return (dispatch: Dispatch<UserAction | HelperAction>) => {
    // Best-effort: revoke the refresh-token session and blocklist the access
    // token server-side. Fire-and-forget so logout never blocks on the network.
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      logoutRequest(refreshToken).catch(() => undefined);
    }
    clearTokens();
    // The Firebase session must not outlive the app session.
    signOutOfFirebase();
    dispatch({
      type: ActionType.SET_INITIAL_USER_STATE,
    });
    dispatch({
      type: ActionType.CLEAR_NAV_ITEMS,
    });
  };
};

export const updateUserDashboards = (dashboards: UserDashboards) => {
  return async (dispatch: Dispatch<UserAction>) => {
    let token = store.getState().helper.access_token;
    try {
      await updateUserDashboardsRequest(token, dashboards);
      dispatch({
        type: ActionType.SET_USER_DATA,
        payload: { dashboards },
      });
    } catch (e) {
      console.error(e);
    }
  };
};

export const userRegister = ({
  data,
  redirectUrl,
  saveToStore,
  recaptcha_token,
  handleServerError,
}: CreateUser) => {
  return async (dispatch: Dispatch<UserAction | HelperAction>) => {
    try {
      dispatch({
        type: ActionType.SET_USER_DATA_LOADING,
        payload: true,
      });
      const result = await createUserRequest(data, recaptcha_token);
      const userData = result.data;
      const initialUserState: User = {
        ...userData,
        isCompanyAdmin: userData.positions?.some((position: CompanyPosition) =>
          isEffectiveAdminPosition(position)
        ),
      };

      const username = data.email || data.phone || '';
      const loginResult = await loginRequest({
        username,
        password: data.password_1,
        client_secret: CAPTCHA_AVOIDER,
      });
      const token = loginResult.data.access_token;

      setTokens(token, loginResult.data.refresh_token);

      if (data.phone) {
        sendVerificationSMSRequest({ recaptcha_token: CAPTCHA_AVOIDER });
      }

      let finalUserState: User = initialUserState;
      let finalRedirect: string | undefined = redirectUrl;

      if (saveToStore) {
        const resolved = await resolvePostAuthState(redirectUrl);
        finalRedirect = resolved.redirect;
        if (resolved.freshUser) {
          finalUserState = {
            ...resolved.freshUser,
            isCompanyAdmin: resolved.freshUser.positions?.some(isEffectiveAdminPosition),
          };
        }
      }

      if (finalRedirect) {
        dispatch({
          type: ActionType.SET_REDIRECT_TO,
          payload: finalRedirect,
        });
      }

      dispatch({
        type: ActionType.SET_ACCESS_TOKEN,
        payload: token,
      });

      if (saveToStore) {
        const helper = store.getState().helper;
        const selectedPosition = getSelectedPosition(finalUserState, helper);
        updateNavItems(finalUserState, dispatch, selectedPosition);
        if (selectedPosition) {
          dispatch({
            type: ActionType.SET_SELECTED_COMPANY,
            payload: selectedPosition.company,
          });
          dispatch({
            type: ActionType.SET_SELECTED_POSITION,
            payload: selectedPosition,
          });
        }
        dispatch({
          type: ActionType.SET_USER_DATA,
          payload: finalUserState,
        });
      }

      dispatch({
        type: ActionType.SET_USER_DATA_LOADING,
        payload: false,
      });
    } catch (error) {
      dispatch({
        type: ActionType.SET_USER_DATA_LOADING,
        payload: false,
      });
      handleServerError(error);
    }
  };
};

export const verifyPhone = ({ code, redirectUrl, skip, handleServerError }: VerifyPhoneData) => {
  return async (dispatch: Dispatch<UserAction | HelperAction>) => {
    try {
      dispatch({
        type: ActionType.SET_USER_DATA_LOADING,
        payload: true,
      });
      if (!skip) {
        await redeemVerificationCodeRequest(+code);
      }
      const resolved = await resolvePostAuthState(redirectUrl);
      if (resolved.redirect) {
        dispatch({
          type: ActionType.SET_REDIRECT_TO,
          payload: resolved.redirect,
        });
      }
      const helper = store.getState().helper;
      const userState = resolved.freshUser
        ? {
            ...resolved.freshUser,
            isCompanyAdmin: resolved.freshUser.positions?.some(isEffectiveAdminPosition),
          }
        : await handleMeRequest();
      const selectedPosition = getSelectedPosition(userState, helper);
      updateNavItems(userState, dispatch, selectedPosition);
      if (selectedPosition) {
        dispatch({
          type: ActionType.SET_SELECTED_COMPANY,
          payload: selectedPosition.company,
        });
        dispatch({
          type: ActionType.SET_SELECTED_POSITION,
          payload: selectedPosition,
        });
      }
      dispatch({
        type: ActionType.SET_USER_DATA,
        payload: userState,
      });
      dispatch({
        type: ActionType.SET_USER_DATA_LOADING,
        payload: false,
      });
    } catch (error) {
      clearUser();
      dispatch({
        type: ActionType.SET_USER_DATA_LOADING,
        payload: false,
      });
      handleServerError(error);
    }
  };
};

export const me = () => {
  return async (dispatch: Dispatch<UserAction | HelperAction>) => {
    //TODO remove token from redux
    const helper = store.getState().helper;
    let token = helper.access_token;
    if (!token) {
      token = getFromLS('token');
      dispatch({
        type: ActionType.SET_ACCESS_TOKEN,
        payload: token,
      });
    }
    try {
      const userState = await handleMeRequest();
      const dashboards = await handleUserDashboardsRequest(token as string);
      const res = await getQualificationsRequest({});
      userState.qualifications = res.data;
      let selectedPosition = getSelectedPosition(userState, helper);
      updateNavItems(userState, dispatch, selectedPosition);
      if (selectedPosition) {
        dispatch({
          type: ActionType.SET_SELECTED_COMPANY,
          payload: selectedPosition.company,
        });
        dispatch({
          type: ActionType.SET_SELECTED_POSITION,
          payload: selectedPosition,
        });
      }
      const isCompanyAdmin = userState.positions?.some(isEffectiveAdminPosition);
      dispatch({
        type: ActionType.SET_USER_DATA,
        payload: {
          ...userState,
          dashboards: {
            ...dashboards,
            teams: {
              ...dashboards.teams,
              widgets: [
                ...(dashboards.teams?.widgets || []),
                // TODO: remove this after deploy to prod
                {
                  name: 'Team Proximity',
                  system_name: 'proximity_to_work',
                  module_name: 'Teams',
                  type: 'custom',
                  available: true,
                  is_active: true,
                  layout: {
                    i: 'proximity_to_work',
                    h: 8,
                    w: 6,
                    x: 0,
                    y: 0,
                    isDraggable: true,
                    isResizable: true,
                  },
                },
              ],
            },
            time: withDormantTeamMembersWidget(dashboards.time),
          },
          isCompanyAdmin,
        },
      });

      dispatch({
        type: ActionType.SET_USER_DATA_LOADING,
        payload: false,
      });

      if (userState.worker?.uuid) {
        const workerUuid = userState.worker.uuid;
        getWorkerRequest(workerUuid)
          .then(res => {
            if (store.getState().user.worker?.uuid === workerUuid) {
              dispatch({
                type: ActionType.SET_USER_DATA,
                payload: { worker: res.data },
              });
            }
          })
          .catch(console.error);
      }
    } catch (error) {
      clearUser();
    }
  };
};

export const userLogin = ({
  username,
  password,
  recaptcha_token,
  handleServerError,
  redirectUrl,
}: LoginData) => {
  return async (dispatch: Dispatch<UserAction | HelperAction>) => {
    try {
      dispatch({
        type: ActionType.SET_USER_DATA_LOADING,
        payload: true,
      });
      const helper = store.getState().helper;
      const result = await loginRequest({ username, password, client_secret: recaptcha_token });
      const token = result.data.access_token;

      updateToken(token, result.data.refresh_token, dispatch);

      const userState = await handleMeRequest();
      const dashboards = await handleUserDashboardsRequest(token as string);

      let selectedPosition = getSelectedPosition(userState, helper);
      updateNavItems(userState, dispatch, selectedPosition);
      if (selectedPosition) {
        dispatch({
          type: ActionType.SET_SELECTED_COMPANY,
          payload: selectedPosition.company,
        });
        dispatch({
          type: ActionType.SET_SELECTED_POSITION,
          payload: selectedPosition,
        });
      }

      dispatch({
        type: ActionType.SET_USER_DATA,
        payload: { ...userState, dashboards },
      });

      dispatch({
        type: ActionType.SET_USER_DATA_LOADING,
        payload: false,
      });
      if (redirectUrl) {
        dispatch({
          type: ActionType.SET_REDIRECT_TO,
          payload: redirectUrl,
        });
      }
    } catch (error) {
      dispatch({
        type: ActionType.SET_USER_DATA_LOADING,
        payload: false,
      });
      handleServerError(error);
    }
  };
};

export const updateUser = ({
  data,
  isNotify,
  callback,
  handleServerError,
  showNotification,
}: UpdateUser) => {
  return async (dispatch: Dispatch<UserAction | HelperAction>) => {
    try {
      dispatch({
        type: ActionType.SET_USER_DATA_LOADING,
        payload: true,
      });
      const result = await updateMeRequest(data);

      dispatch({
        type: ActionType.SET_USER_DATA,
        payload: result.data as unknown as User,
      });
      dispatch({
        type: ActionType.SET_USER_DATA_LOADING,
        payload: false,
      });
      if (isNotify) {
        showNotification({
          message: 'Your data successfully updated',
          options: {
            variant: 'success',
          },
        });
      }
      if (callback) {
        callback();
      }
    } catch (error) {
      handleServerError(error);
    }
  };
};

export const uploadUserAvatar = ({ file, saveToProfile, handleServerError }: UploadAvatarData) => {
  return async (dispatch: Dispatch<UserAction | HelperAction>) => {
    dispatch({
      type: ActionType.SET_USER_DATA_LOADING,
      payload: true,
    });
    try {
      const result = await uploadProfilePhotoRequest(file);
      const photo: S3FileData = result.data;
      let userData = { photo: { url: photo.s3_key_large, icon_url: photo.s3_key_small } };

      if (saveToProfile) {
        await updateProfilePhotoRequest({
          photo_url: photo.s3_key_large,
          avatar_url: photo.s3_key_small,
          file_name: photo.uuid || '',
        });
      }

      dispatch({
        type: ActionType.SET_USER_DATA,
        payload: userData,
      });
      dispatch({
        type: ActionType.SET_USER_DATA_LOADING,
        payload: false,
      });
    } catch (error) {
      dispatch({
        type: ActionType.SET_USER_DATA_LOADING,
        payload: false,
      });
      handleServerError(error);
    }
  };
};

export const updateWorker = ({ showNotification, data, handleServerError }: UpdateWorkerData) => {
  return async (dispatch: Dispatch<UserAction | HelperAction>) => {
    const token = store.getState().helper.access_token;
    const uuid = store.getState().user.worker?.uuid;
    const qualifications = store.getState().user.worker?.qualifications ?? null;
    try {
      dispatch({
        type: ActionType.SET_USER_DATA_LOADING,
        payload: true,
      });

      const result = await updateWorkerRequest(token as string, uuid as string, data);
      const workerData: Worker = { ...result.data, ...qualifications };

      dispatch({
        type: ActionType.SET_USER_DATA,
        payload: { worker: workerData },
      });
      dispatch({
        type: ActionType.SET_USER_DATA_LOADING,
        payload: false,
      });
      showNotification({
        message: 'Your data successfully updated',
        options: {
          variant: 'success',
        },
      });
    } catch (error) {
      handleServerError(error);
    }
  };
};
